import React from 'react';

type MaxWidth = '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';

const MAX_WIDTH: Record<MaxWidth, string> = {
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'max-w-full',
};

interface PortalContainerProps {
  children: React.ReactNode;
  className?: string;
  /** Content column width. Defaults to the portal-wide `7xl`. */
  maxWidth?: MaxWidth;
  /** Drop the standard vertical rhythm — for pages that own their own top spacing
   *  (e.g. a sticky toolbar that must sit flush under the header). */
  bleed?: boolean;
}

/**
 * The single content wrapper for portal pages. Owns the three pieces of page chrome that were
 * previously copy-pasted (and drifting) across every page: the max-width column, the responsive
 * horizontal gutters, and the standard vertical rhythm (`py-6 md:py-8`).
 *
 * Pages render their content through this and declare only what differs — usually just `maxWidth`.
 * This replaces the ad-hoc `container mx-auto px-4 py-6 md:py-8 max-w-Nxl` wrappers, and the portal
 * shell no longer wraps the router outlet, so there is exactly one container per page.
 */
export const PortalContainer: React.FC<PortalContainerProps> = ({
  children,
  className = '',
  maxWidth = '7xl',
  bleed = false,
}) => (
  <div
    className={`${MAX_WIDTH[maxWidth]} mx-auto px-4 sm:px-6 ${bleed ? '' : 'py-6 md:py-8'} ${className}`.trim()}
  >
    {children}
  </div>
);

export default PortalContainer;
